import { type CSSProperties, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { clearDebugTrace, getDebugTraceSnapshot, subscribeDebugTrace } from "utils/debug/debugTrace";

// TEMP diagnostic UI. Shows the [state-debug] feed on the kiosk screen and lets it be copied/shared.
// Mounted only when shouldTraceLightingDebug() is true (master tablet + ?lightingDebug=true flag).

const Z = 2_147_483_647;

const pillStyle: CSSProperties = {
	position: "fixed",
	right: 12,
	bottom: 12,
	zIndex: Z,
	padding: "8px 12px",
	borderRadius: 16,
	border: "none",
	background: "rgba(20,20,20,0.85)",
	color: "#fff",
	font: "600 13px/1 ui-monospace, Menlo, monospace",
};

const panelStyle: CSSProperties = {
	position: "fixed",
	left: 0,
	right: 0,
	bottom: 0,
	zIndex: Z,
	height: "50vh",
	display: "flex",
	flexDirection: "column",
	background: "rgba(15,15,18,0.96)",
	color: "#eaeaea",
	borderTop: "2px solid #444",
	boxShadow: "0 -4px 16px rgba(0,0,0,0.4)",
};

const headerStyle: CSSProperties = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: 8,
	padding: "8px 10px",
	borderBottom: "1px solid #333",
	font: "700 13px/1.2 ui-monospace, Menlo, monospace",
};

const buttonsStyle: CSSProperties = { display: "flex", gap: 6, flexShrink: 0 };

const btnStyle: CSSProperties = {
	padding: "8px 12px",
	borderRadius: 8,
	border: "1px solid #555",
	background: "#2a2a30",
	color: "#fff",
	font: "600 13px/1 ui-monospace, Menlo, monospace",
};

const textareaStyle: CSSProperties = {
	flex: 1,
	width: "100%",
	boxSizing: "border-box",
	margin: 0,
	padding: 8,
	border: "none",
	resize: "none",
	background: "#0d0d10",
	color: "#d7d7d7",
	font: "400 11px/1.45 ui-monospace, Menlo, monospace",
	whiteSpace: "pre",
};

const hintStyle: CSSProperties = {
	padding: "6px 10px",
	font: "400 11px/1.3 ui-monospace, Menlo, monospace",
	color: "#9a9a9a",
	borderTop: "1px solid #333",
};

type CopyState = "idle" | "copied" | "failed";

const copyLabel = (state: CopyState): string => {
	if (state === "copied") {
		return "Copied ✓";
	}
	if (state === "failed") {
		return "Copy blocked — select below";
	}
	return "Copy";
};

export const LightingDebugOverlay = () => {
	const lines = useSyncExternalStore(subscribeDebugTrace, getDebugTraceSnapshot, getDebugTraceSnapshot);
	const [expanded, setExpanded] = useState(false);
	const [copyState, setCopyState] = useState<CopyState>("idle");
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const text = lines.join("\n");

	// biome-ignore lint/correctness/useExhaustiveDependencies: `text` is the intentional trigger — re-scroll to the bottom whenever a new line arrives
	useEffect(() => {
		if (expanded && textareaRef.current) {
			textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
		}
	}, [expanded, text]);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(text);
			setCopyState("copied");
		} catch {
			// Kiosk WKWebViews often block the async clipboard API — fall back to selecting the
			// textarea + execCommand, and leave the text selected so it can be copied by hand.
			const node = textareaRef.current;
			let ok = false;
			if (node) {
				node.focus();
				node.select();
				try {
					ok = document.execCommand("copy");
				} catch {
					ok = false;
				}
			}
			setCopyState(ok ? "copied" : "failed");
		}
		window.setTimeout(() => setCopyState("idle"), 2000);
	};

	if (!expanded) {
		return (
			<button type="button" style={pillStyle} onClick={() => setExpanded(true)}>
				🐞 state-debug ({lines.length})
			</button>
		);
	}

	return (
		<div style={panelStyle}>
			<div style={headerStyle}>
				<span>state-debug ({lines.length})</span>
				<div style={buttonsStyle}>
					<button type="button" style={btnStyle} onClick={handleCopy}>
						{copyLabel(copyState)}
					</button>
					<button type="button" style={btnStyle} onClick={clearDebugTrace}>
						Clear
					</button>
					<button type="button" style={btnStyle} onClick={() => setExpanded(false)}>
						Hide
					</button>
				</div>
			</div>
			<textarea
				ref={textareaRef}
				style={textareaStyle}
				value={text}
				readOnly={true}
				spellCheck={false}
				onFocus={(event) => event.currentTarget.select()}
			/>
			<div style={hintStyle}>If "Copy" is blocked, tap inside the box → Select All → Copy, then paste to share.</div>
		</div>
	);
};
